import { Platform } from 'react-native';

/**
 * CSV Export Utility for Contacts
 * Matches omni-contact structure with identifiers, profile fields, and tags
 */

export interface ContactCSVRow {
  name: string;
  phone?: string;
  email?: string;
  whatsapp?: string;
  'facebook-messenger'?: string;
  instagram?: string;
  telegram?: string;
  tags?: string;
  'location-address'?: string;
}

/**
 * Convert contacts array to CSV format matching omni-contact structure
 */
export const contactsToCSV = (contacts: any[]): string => {
  // CSV Headers matching omni-contact structure
  const headers = [
    'name',
    'phone',
    'email',
    'whatsapp',
    'facebook-messenger',
    'instagram',
    'telegram',
    'tags',
    'location-address',
  ];

  // Build CSV rows
  const rows = contacts.map((contact) => {
    const row: ContactCSVRow = {
      name: contact.name || contact.profile?.name || '',
    };

    // Extract identifiers - omni-contact uses object format: { phone: ['+123'], email: ['test@example.com'] }
    const identifiersObj = contact.identifiers || contact.parentContact?.identifiers;
    
    if (identifiersObj && typeof identifiersObj === 'object') {
      // Handle object format: { phone: ['+123'], email: ['test@example.com'] }
      Object.entries(identifiersObj).forEach(([type, values]) => {
        if (values && Array.isArray(values) && values.length > 0) {
          // Take the first value for each identifier type
          const value = values[0];
          if (value && ['phone', 'email', 'whatsapp', 'facebook-messenger', 'instagram', 'telegram'].includes(type)) {
            row[type as keyof ContactCSVRow] = value;
          }
        } else if (values && typeof values === 'string') {
          // Handle single string value
          if (['phone', 'email', 'whatsapp', 'facebook-messenger', 'instagram', 'telegram'].includes(type)) {
            row[type as keyof ContactCSVRow] = values;
          }
        }
      });
    }

    // Fallback to direct contact fields (only if not already set from identifiers)
    if (!row.phone && (contact.phoneNumber || contact.phone)) {
      row.phone = contact.phoneNumber || contact.phone;
    }
    if (!row.email && contact.email) {
      row.email = contact.email;
    }
    if (!row.whatsapp && (contact.whatsapp || contact.whatsappNumber)) {
      row.whatsapp = contact.whatsapp || contact.whatsappNumber;
    }
    if (!row['facebook-messenger'] && contact.facebookId) {
      row['facebook-messenger'] = contact.facebookId;
    }

    // Extract tags
    const tags = contact.profile?.tags || contact.tags || [];
    if (Array.isArray(tags) && tags.length > 0) {
      row.tags = tags.join('|');
    } else if (typeof tags === 'string') {
      row.tags = tags;
    }

    // Extract location
    const location = contact.profile?.location || contact.location;
    if (location?.address) {
      row['location-address'] = location.address;
    }

    return row;
  });

  // Convert to CSV string
  const csvRows = [
    headers.join(','),
    ...rows.map((row) =>
      headers.map((header) => {
        const value = row[header as keyof ContactCSVRow] || '';
        // Escape commas and quotes in CSV values
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          return `"${String(value).replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    ),
  ];

  return csvRows.join('\n');
};

/**
 * Generate empty CSV template matching omni-contact structure — every
 * column the mapper/bulk-upload backend actually recognizes (see
 * bulk-contact.controller.ts's ParsedContact and
 * bulk-contact-mapper.util.ts's TARGET_ALIASES), so a filled-in template
 * auto-maps every column on upload instead of needing manual remapping for
 * columns the template itself omitted.
 */
export const generateEmptyCSVTemplate = (): string => {
  const headers = [
    'name',
    'phone',
    'email',
    'whatsapp',
    'facebook-messenger',
    'instagram',
    'telegram',
    'tags',
    'location-address',
    'location-city',
    'location-country',
    // A pasted Google/Apple Maps share link — resolved server-side into
    // address/city/country (+ coordinates) at import time, an alternative
    // to filling in location-address/city/country by hand. Only used when
    // those three are left blank for a row.
    'location-url',
    'nationality',
  ];

  const exampleRow = [
    'John Doe',
    '+1234567890',
    'john@example.com',
    '+1234567890',
    '',
    '',
    '',
    'customer|vip',
    '123 Main St',
    'Cairo',
    'Egypt',
    '',
    'Egyptian',
  ];

  return [
    headers.join(','),
    exampleRow.map((value) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${String(value).replace(/"/g, '""')}"`;
      }
      return value;
    }).join(','),
  ].join('\n');
};

/**
 * Download CSV file
 */
export const downloadCSV = (csvContent: string, filename: string): void => {
  if (Platform.OS === 'web') {
    // Web: Create blob and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } else {
    // Mobile: Use FileSystem or share
    console.warn('[CSV Export] Mobile CSV download not yet implemented. Content:', csvContent.substring(0, 100));
    // TODO: Implement mobile CSV download using expo-file-system and sharing
  }
};

/**
 * Downloads a binary file (e.g. the .xlsx export response body) — separate
 * from downloadCSV since a Blob built from a plain string can't hold binary
 * bytes correctly; this takes the response's own Blob straight from
 * fetch/Response.blob() instead of re-encoding text.
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  if (Platform.OS === 'web') {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } else {
    console.warn('[CSV Export] Mobile file download not yet implemented for', filename);
    // TODO: Implement mobile file download using expo-file-system and sharing
  }
};
